/* iceberg-ui */
import {
    Box,
    Flex,
    Form,
    FormFieldGroup,
    FormModalFooter,
    type FormSubmitHandler
} from '@auviknetworks/iceberg-ui';

/* MUI */
import { Alert, CircularProgress, Grid } from '@mui/material';

/* Shared */
import { flattenObject } from 'application/Home/GlobalAdmin/SaasOps/flattenObject';
import { type FieldSchemaJson } from 'application/Shared/Components/Form/Field.type';
import { FieldFactory } from 'application/Shared/Components/Form/FieldFactory';
import { type FactoryVariant } from 'application/Shared/Components/Form/types';
import { RECIPE_ACTIONS } from 'application/Shared/Constants/Workato';
import { FormFactoryContextProvider } from 'application/Shared/Contexts/FormFactoryContext';
import { Notify, Route } from 'application/Shared/Helpers';
import { debug as logger } from 'application/Shared/Utils';

/* Types */
import { User } from 'application/Shared/types/user';

/* Hooks */
import { useUserTheme } from 'hooks/dataAccess/User/userTheme';
import {
    useRecipeDetails,
    useRunCustomerRecipe
} from 'hooks/dataAccess/Workato';

/* React */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';

import { mockConnectionData } from '../mockData';
import { MockConnectionData, WorkatoActions } from '../types';

/* Components */
import { FormModalButton } from './FormModalButton';
import { Markdown } from './Markdown';
import {
    APP_LIST_WIDTH,
    CALCULATED_GRID_HEIGHT_WITH_FORM_SUBMIT
} from './constants';
import {
    AllowedActions,
    SaasOpsActionItem,
    SaasOpsActionItemStatus,
    SaasOpsActionStatus
} from './types';

const CancelSkipOrRemoveButton = ({
    action,
    appState,
    removeApp,
    reviewAppLater,
    singleAppDisplay,
    step
}: {
    action: AllowedActions;
    appState: SaasOpsActionItem;
    markAppAsCompleted: () => void;
    removeApp: (applicationId: string) => void;
    reviewAppLater: () => void;
    singleAppDisplay?: boolean;
    step: FactoryVariant;
}) => {
    const { t } = useTranslation();

    if (step !== 'summary') {
        if (singleAppDisplay) {
            return (
                <Link to=".">
                    <FormModalButton<FormValues>
                        data-trackid={`${action}User-cancel`}
                        type="button"
                        variant="secondary"
                    >
                        {t('Common.cancel')}
                    </FormModalButton>
                </Link>
            );
        }

        if (appState.status === SaasOpsActionItemStatus.Pending) {
            return (
                <FormModalButton<FormValues>
                    data-trackid={`${action}User-reviewLater`}
                    type="button"
                    variant="secondary"
                    onClick={reviewAppLater}
                >
                    {t('SaasOps.form.button.reviewLater')}
                </FormModalButton>
            );
        }

        if (appState.status === SaasOpsActionItemStatus.Skipped) {
            return (
                <FormModalButton<FormValues>
                    data-trackid={`${action}User-removeApp`}
                    type="button"
                    variant="secondary"
                    onClick={() => removeApp(appState.applicationId)}
                >
                    {t('Common.remove')}
                </FormModalButton>
            );
        }
    }

    return <Box />;
};

const convertActionType = (action: AllowedActions): RECIPE_ACTIONS => {
    if (action === WorkatoActions.offboard) {
        return RECIPE_ACTIONS.OFFBOARD;
    }

    return RECIPE_ACTIONS.ONBOARD;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- fix with type from schema
type FormValues = Record<string, any>;

export const SaasOpsActionForm = ({
    action,
    appState,
    goToNextApp,
    markAppAsCompleted,
    markAppAsError,
    overallSaasOpsActionStatus,
    removeApp,
    reviewAppLater,
    singleAppDisplay,
    userDetails
}: {
    action: AllowedActions;
    appState: SaasOpsActionItem;
    goToNextApp: () => void;
    markAppAsCompleted: () => void;
    markAppAsError: () => void;
    overallSaasOpsActionStatus: SaasOpsActionStatus;
    reviewAppLater: () => void;
    removeApp: (applicationId: string) => void;
    singleAppDisplay?: boolean;
    userDetails: Partial<User> | null;
}) => {
    const { t } = useTranslation();
    const { clientId, partnerId } = useParams();

    const [step, setStep] = useState<FactoryVariant>(
        appState.status === SaasOpsActionItemStatus.Completed
            ? 'summary'
            : 'form'
    );
    const [message, setMessage] = useState<
        { content: string; level: 'success' | 'error' | 'info' } | undefined
    >();
    const { recipe } = appState;
    const { applicationId } = recipe;
    const workatoProvider = recipe?.application?.workatoProvider || '';

    // Find the connection data for the current Workato provider
    const connectionInfo = mockConnectionData?.find(
        (conn) => conn.name === workatoProvider
    ) as MockConnectionData;

    const userTheme = useUserTheme();
    const imgSrc = `/images/${userTheme}/integrations/saasOps/${connectionInfo?.image}`;

    const [formDefaultValues] = useState({
        user: userDetails,
        userEmail: userDetails?.email,
        ...(flattenObject({
            user: userDetails
        }) as Record<string | number, string>)
    });

    // TODO: Remove this `convertActionType` function - see https://auviknetworks.atlassian.net/browse/SAAS-2133
    const recipeAction = convertActionType(action);

    const {
        data: recipeDetails,
        error: recipeDetailsError,
        loading: recipeLoading
    } = useRecipeDetails({
        action: recipeAction,
        applicationId: applicationId!,
        platformTenantId: clientId!
    });

    const baseAdminRoute = Route.buildBaseAdminRoute({
        clientId: clientId!,
        partnerId
    });
    const pagePath = `${baseAdminRoute}/saasOps/${action}`;

    // We set the message based on the step
    useEffect(() => {
        if (step === 'summary') {
            setMessage({
                content: `SaasOps.${action}.success`,
                level: 'success'
            });
        } else if (connectionInfo?.integrationMessage) {
            setMessage({
                content: connectionInfo.integrationMessage,
                level: 'info'
            });
        }
    }, [action, connectionInfo?.integrationMessage, step]);

    const { runRecipe } = useRunCustomerRecipe();

    const submitHandler: FormSubmitHandler<FormValues> = async (
        values,
        _,
        methods
    ) => {
        if (!values?.user?.email && !values?.user_email && !values.userEmail) {
            setMessage({
                content: 'ConfigureSelfUser.error.email',
                level: 'error'
            });
            return;
        }

        const { error, data, workatoError } = await runRecipe({
            platformTenantId: clientId!,
            recipeId: recipe.id,
            recipeParams: values
        });

        if (data && !error) {
            // Reset the form and show the summary based on the data send to the backend
            methods?.reset(values);
            Notify.s(t(`SaasOps.${action}.success`));
            setStep('summary');

            markAppAsCompleted();
        }

        if (error || workatoError) {
            setMessage({
                content: workatoError || error || `SaasOps.${action}.failed`,
                level: 'error'
            });
            markAppAsError();
            Notify.e(t(`SaasOps.${action}.failed`));
            logger.error(error);
        }
    };

    return (
        <Grid
            item
            flexGrow={1}
            sx={{
                // If these height values are changed, it is very likely the height values in the AppsList will need to be changed as wel.
                maxHeight: CALCULATED_GRID_HEIGHT_WITH_FORM_SUBMIT,
                minHeight: CALCULATED_GRID_HEIGHT_WITH_FORM_SUBMIT,
                overflowY: 'auto'
            }}
        >
            <Box p={3}>
                {connectionInfo?.image && (
                    <img
                        data-testid={`appLogo-${workatoProvider}`}
                        alt={connectionInfo.displayName}
                        src={imgSrc}
                        style={{
                            height: '48px',
                            marginBottom: '24px',
                            width: 'fit-content'
                        }}
                    />
                )}

                {connectionInfo?.[`${action}Content`] && (
                    <Markdown content={connectionInfo[`${action}Content`]!} />
                )}
            </Box>

            {recipeLoading && (
                <Flex p={3} justifyContent="center">
                    <CircularProgress />
                </Flex>
            )}

            {!recipeLoading &&
                !recipeDetailsError &&
                userDetails &&
                clientId && (
                    <FormFactoryContextProvider
                        applicationId={applicationId!}
                        clientId={clientId}
                    >
                        <Form<FormValues>
                            defaultValues={formDefaultValues}
                            onSubmit={submitHandler}
                        >
                            <Box p={3}>
                                {/* Display integration message if available, and when we don't need to focus on errors */}
                                {message && (
                                    <Alert
                                        severity={message.level}
                                        icon={false}
                                        sx={{
                                            justifyContent: 'center',
                                            marginBottom: '24px'
                                        }}
                                    >
                                        {t(message.content)}
                                    </Alert>
                                )}

                                <FormFieldGroup>
                                    {recipeDetails?.requestSchema.map(
                                        (field: FieldSchemaJson) => (
                                            <FieldFactory
                                                key={field.name}
                                                schema={field}
                                                variant={step}
                                            />
                                        )
                                    )}
                                </FormFieldGroup>
                            </Box>
                            <Box
                                sx={{
                                    bottom: 0,
                                    // TODO: If we hide the app list when there's only a single app, then this will need to adjust to 0.
                                    left: singleAppDisplay ? 0 : APP_LIST_WIDTH,
                                    position: 'absolute',
                                    right: 0
                                }}
                            >
                                <FormModalFooter>
                                    <CancelSkipOrRemoveButton
                                        action={action}
                                        appState={appState}
                                        markAppAsCompleted={markAppAsCompleted}
                                        removeApp={removeApp}
                                        reviewAppLater={reviewAppLater}
                                        singleAppDisplay={singleAppDisplay}
                                        step={step}
                                    />
                                    {step === 'form' &&
                                        overallSaasOpsActionStatus !==
                                            SaasOpsActionStatus.Completed && (
                                            <FormModalButton<FormValues>
                                                data-trackid={`${action}User-confirmDetails`}
                                                type="submit"
                                            >
                                                {t(`Users.actions.${action}`, {
                                                    firstName:
                                                        userDetails?.firstName,
                                                    lastName:
                                                        userDetails?.lastName
                                                })}
                                            </FormModalButton>
                                        )}
                                    {step === 'summary' &&
                                        overallSaasOpsActionStatus !==
                                            SaasOpsActionStatus.Completed && (
                                            <FormModalButton<FormValues>
                                                data-trackid={`${action}User-completed`}
                                                onClick={goToNextApp}
                                            >
                                                {t('Common.next')}
                                            </FormModalButton>
                                        )}
                                    {overallSaasOpsActionStatus ===
                                        SaasOpsActionStatus.Completed && (
                                        <Link to={pagePath}>
                                            <FormModalButton<FormValues>
                                                data-trackid={`${action}User-done`}
                                            >
                                                {t('Common.done')}
                                            </FormModalButton>
                                        </Link>
                                    )}
                                </FormModalFooter>
                            </Box>
                        </Form>
                    </FormFactoryContextProvider>
                )}
        </Grid>
    );
};
